<template>
    <el-dialog
        title="历史数据"
        :model-value="modelValue"
        top="10vh"
        width="1100px"
        @open="dialogOpen"
        @close="closed"
        :close-on-click-modal="false"
    >
        <div class="tool-box">
            <div class="search-box">
                <el-date-picker
                    v-model="date_range"
                    type="datetimerange"
                    range-separator="至"
                    start-placeholder="开始日期"
                    end-placeholder="结束日期"
                    value-format="YYYY-MM-DD HH:mm:ss"
                    style="margin-right: 10px"
                />
                <el-button type="primary" :icon="Search" @click="getRecordData">搜索</el-button>
            </div>
            <el-button type="primary" @click="handleExport" :icon="Upload" v-permission="['factory:monitors:record-export']">
                数据导出
            </el-button>
        </div>
        <el-table :data="table_data" border v-loading="tableLoading" row-key="id">
            <el-table-column label="IMEI" min-width="150">
                <template #default>{{ props.deviceData.IMEI }}</template>
            </el-table-column>
            <el-table-column label="上传时间" prop="ts" min-width="150"></el-table-column>
            <el-table-column label="采集时间" prop="data_ts" min-width="150"></el-table-column>
            <el-table-column label="上传周期" prop="report_cycle" width="110px">
                <template #header>
                    <p>上传周期</p>
                    <p>(分钟)</p>
                </template>
                <template #default="{ row }">{{ row.report_cycle }}</template>
            </el-table-column>
            <el-table-column label="信号强度" prop="csq">
                <template #default="{ row }">
                    <div style="display: flex">
                        <span>{{ row.csq }}</span>
                        &nbsp;
                        <div class="csq">
                            <span
                                v-for="num of 5"
                                class="bar"
                                :key="num"
                                :class="['bar' + num, num <= Math.floor(row.csq / 6) ? 'active' : '']"
                            ></span>
                        </div>
                    </div>
                </template>
            </el-table-column>
            <el-table-column label="电池" width="80px" prop="bat">
                <template #header>
                    <p>电池</p>
                    <p>(%)</p>
                </template>
                <template #default="{ row }">
                    <span v-if="row.bat <= 100">{{ row.bat }}</span>
                    <span v-else></span>
                </template>
            </el-table-column>
            <el-table-column label="告警标记" prop="flags" min-width="150">
                <template #default="{ row }">
                    <span v-show="(row.flags & (1 << 7)) !== 0">掉电故障</span>
                    <span v-show="(row.flags & (1 << 7)) !== 0 && (row.flags & (1 << 6)) !== 0">，</span>
                    <span v-show="(row.flags & (1 << 6)) !== 0">移位故障</span>
                </template>
            </el-table-column>
            <el-table-column label="室温（℃）" prop="roottemp" min-width="110">
                <template #header>
                    <p>室温</p>
                    <p>(℃)</p>
                </template>
                <template #default="{ row }">{{ row.roottemp }}</template>
            </el-table-column>
            <el-table-column label="湿度（%）" prop="humidity" min-width="110">
                <template #header>
                    <p>湿度</p>
                    <p>（%）</p>
                </template>
                <template #default="{ row }">
                    <template v-if="row?.humidity <= 100">{{ row.humidity }}%</template>
                    <template v-else>--</template>
                </template>
            </el-table-column>
        </el-table>
        <el-pagination
            class="elPagination"
            @size-change="handleSizeChange"
            @current-change="handleCurrentChange"
            :current-page="queryForms.page"
            :page-size="queryForms.page_size"
            :page-sizes="[10, 20, 50, 100]"
            layout="total, sizes, prev, pager, next, jumper"
            :total="total"
        />
    </el-dialog>
</template>
<script setup>
import { defineEmits, defineProps, ref } from 'vue'
import { getDeviceRecord } from '@/api/heating/monitoring.js'
import { ElLoading, ElMessage, ElMessageBox } from 'element-plus'
import { Search, Upload } from '@element-plus/icons'

const props = defineProps({
    modelValue: {
        type: Boolean,
        required: true
    },
    deviceData: {
        type: Object,
        required: true
    }
})
const emits = defineEmits(['update:modelValue', 'updateForm'])

// 弹窗打开
function dialogOpen() {
    getRecordData()
}

// 表格数据
const table_data = ref([])
const tableLoading = ref(false)
function getRecordData() {
    tableLoading.value = true
    const params = queryForms.value
    params.start_time = date_range.value[0]
    params.end_time = date_range.value[1]
    getDeviceRecord({
        device_id: props.deviceData.id,
        ...params
    }).then((data) => {
        let device_data = data[0]
        if (device_data.lists.length > 0) {
            table_data.value = device_data.lists
            total.value = device_data.total
        } else {
            table_data.value = []
            total.value = 0
        }
        tableLoading.value = false
    })
}

const date_range = ref(['', ''])

const queryForms = ref({
    page: 1,
    page_size: 50
})
const total = ref(0)
const handleSizeChange = (currentSize) => {
    queryForms.value.page_size = currentSize
    getRecordData()
}
const handleCurrentChange = (currentPage) => {
    queryForms.value.page = currentPage
    getRecordData()
}

// 详细数据导出
function handleExport() {
    ElMessageBox.confirm('确定导出当前日期范围的历史采集数据？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
    }).then(async () => {
        const loading = ElLoading.service({
            lock: true,
            text: '正在导出...',
            spinner: 'Loading',
            background: 'rgba(0, 0, 0, 0.7)'
        })

        let list = []
        await getDeviceRecord({
            start_time: date_range.value[0],
            end_time: date_range.value[1],
            page: 1,
            page_size: 10000,
            device_id: props.deviceData.id
        }).then((data) => {
            list = data[0].lists
        })
        if (list.length <= 0) {
            loading.close()
            return ElMessage.error('没有数据可导出')
        }

        // 创建excel并写入数据
        let workbook = null
        workbook = new Exceljs.Workbook()
        workbook.created = new Date()
        workbook.modified = new Date()
        workbook.lastPrinted = new Date()
        const worksheet = workbook.addWorksheet('订单', {
            properties: {
                defaultRowHeight: 22
            },
            pageSetup: {
                horizontalCentered: true
            }
        })

        worksheet.columns = [
            {
                header: 'IMEI',
                key: 'IMEI',
                width: 20
            },
            {
                header: '上传时间',
                key: 'ts',
                width: 22
            },
            {
                header: '采集时间',
                key: 'data_ts',
                width: 22
            },
            {
                header: '室温（℃）',
                key: 'roottemp',
                width: 15
            },
            {
                header: '湿度（%）',
                key: 'humidity',
                width: 15
            },
            {
                header: '信号强度',
                key: 'csq',
                width: 15
            },
            {
                header: '电池电量（%）',
                key: 'bat',
                width: 15
            },
            {
                header: '告警标记',
                key: 'alarm_flags',
                width: 20
            }
        ]

        worksheet.addRows(
            list.map((item) => {
                return {
                    IMEI: props.IMEI,
                    ts: item.ts,
                    data_ts: item.data_ts,
                    roottemp: item.roottemp,
                    humidity: item.humidity <= 100 ? item.humidity : '',
                    csq: item.csq,
                    bat: item.bat,
                    alarm_flags:
                        ((item.flags & (1 << 7)) !== 0 ? '掉电故障' : '') +
                        ((item.flags & (1 << 7)) !== 0 && (item.flags & (1 << 6)) !== 0 ? '，' : '') +
                        ((item.flags & (1 << 6)) !== 0 ? '移位故障' : '')
                }
            })
        )

        worksheet.getRow(1).alignment = {
            horizontal: 'center',
            vertical: 'middle'
        }

        // 导出excel文件
        await workbook.xlsx.writeBuffer().then((buffer) => {
            const date = new Date()
            const year = date.getFullYear() // 年
            let month = date.getMonth() + 1 // 月
            let day = date.getDate() // 日
            let hour = date.getHours() // 时
            let minutes = date.getMinutes() // 分
            let seconds = date.getSeconds() // 秒
            // 给一位数的数据前面加 “0”
            if (month >= 1 && month <= 9) month = '0' + month
            if (day >= 0 && day <= 9) day = '0' + day
            if (hour >= 0 && hour <= 9) hour = '0' + hour
            if (minutes >= 0 && minutes <= 9) minutes = '0' + minutes
            if (seconds >= 0 && seconds <= 9) seconds = '0' + seconds
            const presentDate = `室温采集设备监测数据${year}${month}${day}_${hour}${minutes}${seconds}`

            // 导出并存储文件
            FileSaver.saveAs(new Blob([buffer]), `${presentDate}.xlsx`)
        })
        loading.close()
    })
}

// 关闭
function closed() {
    date_range.value = ['', '']
    table_data.value = []
    queryForms.value = {
        page: 1,
        page_size: 50
    }
    emits('update:modelValue', false)
}
</script>

<style scoped>
.tool-box {
    display: flex;
    justify-content: space-between;
    padding-bottom: 15px;
}

.tool-box .search-box {
    display: flex;
    align-items: center;
}

.elPagination {
    float: none !important;
    padding: 15px 0;
    display: flex;
    justify-content: center;
    align-items: center;
}
</style>
